/**
 * The naming coverage card's split (TMMAPP-0163 item 5): how the person's bank texts were named,
 * from `naming-status.bySource`, in the free-first order (your rules, known merchants, the bank
 * text, AI, then unnamed). One line names each non-zero part and leaves zeros out (TMMAPP-0162
 * item 8's rule); the segmented bar draws the same parts in the same order. A named part paints its
 * categorical `line` token (audited on `surface`), unnamed and kept unnamed (TMMAPP-0164 item 12)
 * are the track's `hover`, and a 2px gap of
 * the card shows between parts. Colour never carries a part alone: the line beside the bar says
 * each count, with a swatch matching its segment, and the bar's own name is the same list.
 */
import { Box, Stack, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { PayeeNamingBySource } from '@/api/payeeSuggestions';
import { resolveTokens } from '@/theme';
import { SPLIT_ORDER, splitParts, type SplitKey } from './namingSplitParts';

const PART_KEYS = {
  RULE: 'naming.split.RULE',
  KNOWN_MERCHANT: 'naming.split.KNOWN_MERCHANT',
  BANK_TEXT: 'naming.split.BANK_TEXT',
  AI: 'naming.split.AI',
  UNNAMED: 'naming.split.UNNAMED',
  KEPT_UNNAMED: 'naming.split.KEPT_UNNAMED',
} as const satisfies Record<SplitKey, string>;

export function NamingSplit({ bySource }: { bySource: PayeeNamingBySource }) {
  const { t } = useTranslation();
  const tokens = resolveTokens(useTheme());
  const parts = splitParts(bySource);
  if (parts.length === 0) return null;
  const unnamed = (key: SplitKey) => key === 'UNNAMED' || key === 'KEPT_UNNAMED';
  const colour = (key: SplitKey) =>
    unnamed(key) ? tokens.hover : tokens.categorical[SPLIT_ORDER.indexOf(key)]!.line;
  const texts = parts.map((part) => t(PART_KEYS[part.key], { count: part.count }));

  return (
    <Stack spacing={0.75} data-naming-split="">
      <Typography
        variant="body2"
        component="p"
        sx={{ color: 'text.secondary', display: 'flex', flexWrap: 'wrap', columnGap: 1.5 }}
      >
        {parts.map((part, index) => (
          <Box
            component="span"
            key={part.key}
            data-split-part={part.key}
            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}
          >
            <Box
              component="span"
              aria-hidden
              sx={{
                width: 8,
                height: 8,
                borderRadius: '2px',
                flexShrink: 0,
                bgcolor: colour(part.key),
                ...(unnamed(part.key) ? { outline: `1px solid ${tokens.lineStrong}` } : {}),
              }}
            />
            {texts[index]}
          </Box>
        ))}
      </Typography>
      <Box
        role="img"
        aria-label={t('naming.split.label', { parts: texts.join(', ') })}
        data-naming-split-bar=""
        sx={{ display: 'flex', gap: '2px', height: 8 }}
      >
        {parts.map((part) => (
          <Box
            key={part.key}
            data-split-segment={part.key}
            sx={{
              flexGrow: part.count,
              flexBasis: 0,
              minWidth: 4,
              borderRadius: '2px',
              bgcolor: colour(part.key),
            }}
          />
        ))}
      </Box>
    </Stack>
  );
}
